import React, { useEffect, useRef, useState } from "react";
import { vehicleDisplayIdentifier } from "../../utils/vehicleIdentifier";

import {
  Bars2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useDrag, useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import Modal from "../../layouts/Modal";
import { IDismantlingBay } from "../../models/DismantlingBay";
import { DragTypes } from "../../models/DragTypes";
import { IPlannerVehicleRow } from "../../models/ProductionPlanner";
import classNames from "../../utils/classNames";
import { complexityChipClasses, complexityLabel } from "../../utils/vehicleDisplay";

interface DraggableVehicleRowProps {
  vehicle: IPlannerVehicleRow;
  index: number;
  total: number;
  onMove: (from: number, to: number) => void;
}

function DraggableVehicleRow({ vehicle, index, total, onMove }: DraggableVehicleRowProps) {
  const { t } = useTranslation("production");
  const ref = useRef<HTMLLIElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DragTypes.PLANNER_BAY_ORDER,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index]
  );

  const [, drop] = useDrop(
    () => ({
      accept: DragTypes.PLANNER_BAY_ORDER,
      hover(item: { index: number }) {
        if (item.index === index) return;
        onMove(item.index, index);
        item.index = index;
      },
    }),
    [index, onMove]
  );

  drag(drop(ref));

  return (
    <li
      ref={ref}
      className={classNames(
        "flex items-center gap-2 px-3 py-2 bg-white cursor-grab select-none",
        isDragging ? "opacity-40" : ""
      )}
    >
      <Bars2Icon className="h-4 w-4 text-gray-300 shrink-0" />
      <span className="text-sm text-gray-400 tabular-nums w-6 shrink-0">{index + 1}.</span>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900 truncate">
          {vehicleDisplayIdentifier(vehicle) || t("No VRM")}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {[vehicle.combinedMake, vehicle.combinedModel].filter(Boolean).join(" ")}
        </div>
      </div>

      <span
        className={classNames(
          "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0",
          complexityChipClasses(vehicle.complexity)
        )}
      >
        {t(complexityLabel(vehicle.complexity))}
      </span>

      <div className="flex gap-1 ml-2 shrink-0">
        <button
          type="button"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          className="p-0.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed"
          title={t("Move up")}
        >
          <ChevronUpIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          className="p-0.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed"
          title={t("Move down")}
        >
          <ChevronDownIcon className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

interface BayReorderModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bay: IDismantlingBay;
  date: string;
  // Already in dismantling order — the order the operator works to.
  vehicles: IPlannerVehicleRow[];
  onReorder: (bayId: string, date: string, vehicleIds: string[]) => void;
}

export default function BayReorderModal({
  open,
  setOpen,
  bay,
  date,
  vehicles,
  onReorder,
}: BayReorderModalProps) {
  const { t } = useTranslation("production");
  const [order, setOrder] = useState<IPlannerVehicleRow[]>(vehicles);

  // Start from the live order each time the modal opens, so a background refetch
  // while it's closed doesn't leave a stale list behind.
  useEffect(() => {
    if (!open) return;
    setOrder(vehicles);
  }, [open, vehicles]);

  const moveRow = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    setOrder((current) => {
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleSave = () => {
    onReorder(
      bay.id,
      date,
      order.map((v) => v.id)
    );
    setOpen(false);
  };

  const formattedDate = DateTime.fromISO(date)
    .setLocale("en-gb")
    .toLocaleString(DateTime.DATE_HUGE);

  return (
    <Modal width="max-w-xl" open={open} setOpen={setOpen}>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            {t("Re-order {{name}}", { name: bay.name })}
          </h1>
          <p className="text-sm text-gray-500">{formattedDate}</p>
        </div>
      </div>

      {order.length === 0 ? (
        <div className="mt-6 mb-2 text-center text-sm text-gray-500">
          {t("No vehicles allocated to this line for this day.")}
        </div>
      ) : (
        <>
          <p className="mt-3 text-xs text-gray-500">
            {t("Drag a vehicle to set the order it will be dismantled in.")}
          </p>
          <ul className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
            {order.map((v, index) => (
              <DraggableVehicleRow
                key={v.id}
                vehicle={v}
                index={index}
                total={order.length}
                onMove={moveRow}
              />
            ))}
          </ul>
        </>
      )}

      <div className="flex items-center justify-end gap-x-2 pt-5">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={order.length === 0}
          className={classNames(
            "text-sm px-3 py-1.5 rounded-md bg-primary-600 text-white hover:bg-primary-700",
            order.length === 0 ? "opacity-60 cursor-not-allowed" : ""
          )}
        >
          {t("Save order")}
        </button>
      </div>
    </Modal>
  );
}
